// src/pages/Dashboard.js - ACTUALIZADO CON FILTROS DE MES
import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { 
  getUserTransactions, 
  getExpenseCategories,
  updateInitialMoney,
  createTransaction,
  deleteTransaction,
  getFamilyTransactions
} from '../lib/supabase'
import { 
  Plus, 
  Users,
  Settings,
  PieChart,
  BarChart3,
  Calendar,
  List
} from 'lucide-react'
import InitialMoneyModal from '../components/InitialMoneyModal'
import AddTransactionModal from '../components/AddTransactionModal'
import FamilySection from '../components/FamilySection'
import TransactionCard from '../components/TransactionCard'
import TransactionsByMonth from '../components/TransactionsByMonth'
import MonthFilter from '../components/MonthFilter'
import StatsCards from '../components/StatsCards'

const Dashboard = () => {
  const { profile, refreshProfile } = useAuth()
  const [transactions, setTransactions] = useState([])
  const [familyTransactions, setFamilyTransactions] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [showInitialMoneyModal, setShowInitialMoneyModal] = useState(false)
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false)
  const [activeTab, setActiveTab] = useState('personal')
  
  // Nuevos estados para filtros
  const [selectedMonth, setSelectedMonth] = useState(null)
  const [viewMode, setViewMode] = useState('grouped') // 'grouped' o 'list'

  const loadDashboardData = useCallback(async () => {
    if (!profile) return

    setLoading(true)
    try {
      // Cargar transacciones personales
      const { data: userTransactions } = await getUserTransactions(profile.id)
      setTransactions(userTransactions || [])

      // Cargar categorías
      const { data: categoriesData } = await getExpenseCategories()
      setCategories(categoriesData || [])

      // Cargar transacciones familiares si pertenece a una familia
      if (profile.family_id) {
        const { data: familyTxns } = await getFamilyTransactions()
        setFamilyTransactions(familyTxns || [])
      }
    } catch (error) {
      console.error('Error cargando datos:', error)
    } finally {
      setLoading(false)
    }
  }, [profile])

  useEffect(() => {
    loadDashboardData()
  }, [loadDashboardData])

  useEffect(() => {
    // Mostrar modal de dinero inicial si es la primera vez
    if (profile && profile.initial_money === 0) {
      setShowInitialMoneyModal(true)
    }
  }, [profile])

  // Filtrar transacciones por mes
  const filterTransactionsByMonth = (transactions, monthKey) => {
    if (!monthKey) return transactions
    
    return transactions.filter(transaction => {
      const date = new Date(transaction.transaction_date)
      const transactionMonthKey = `${date.getFullYear()}-${date.getMonth()}`
      return transactionMonthKey === monthKey
    })
  }

  const handleUpdateInitialMoney = async (amount) => {
    try {
      await updateInitialMoney(profile.id, amount)
      await refreshProfile()
      setShowInitialMoneyModal(false)
    } catch (error) {
      console.error('Error actualizando dinero inicial:', error)
    }
  }

  const handleAddTransaction = async (transactionData) => {
    try {
      const newTransaction = {
        ...transactionData,
        user_id: profile.id
      }
      
      await createTransaction(newTransaction)
      await loadDashboardData()
      await refreshProfile()
      setShowAddTransactionModal(false)
    } catch (error) {
      console.error('Error añadiendo transacción:', error)
    }
  }

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta transacción?')) {
      return
    }
    
    try {
      await deleteTransaction(transactionId)
      await loadDashboardData()
      await refreshProfile()
    } catch (error) {
      console.error('Error eliminando transacción:', error)
    }
  }

  const calculateStats = (txns) => {
    const totalIncome = txns
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0)
    
    const totalExpenses = txns
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0)

    return { totalIncome, totalExpenses }
  }

  const getExpensesByCategory = (txns) => {
    const expensesByCategory = {}
    txns
      .filter(t => t.type === 'expense')
      .forEach(t => {
        const categoryName = t.expense_categories?.name || 'Sin categoría'
        expensesByCategory[categoryName] = (expensesByCategory[categoryName] || 0) + parseFloat(t.amount)
      })
    return expensesByCategory
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando tu área financiera...</p>
        </div>
      </div>
    )
  }

  // Obtener transacciones filtradas según la pestaña activa
  const currentTransactions = activeTab === 'personal' ? transactions : familyTransactions
  const filteredTransactions = filterTransactionsByMonth(currentTransactions, selectedMonth)
  const stats = calculateStats(filteredTransactions)
  const expensesByCategory = getExpensesByCategory(filteredTransactions)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Mi Área Financiera
            </h1>
            <p className="text-gray-600 mt-1">
              Hola {profile?.username}, aquí tienes el resumen de tus finanzas
            </p>
          </div>
          
          <div className="flex space-x-3 mt-4 sm:mt-0">
            <button
              onClick={() => setShowInitialMoneyModal(true)}
              className="flex items-center space-x-1 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              <Settings className="h-4 w-4" />
              <span>Configurar</span>
            </button>
            
            <button
              onClick={() => setShowAddTransactionModal(true)}
              className="flex items-center space-x-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Añadir Movimiento</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <StatsCards 
          profile={profile}
          stats={stats}
          expensesByCategory={expensesByCategory}
        />

        {/* Tabs */}
        <div className="mt-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('personal')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'personal'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <BarChart3 className="h-4 w-4" />
                  <span>Mis Movimientos</span>
                </div>
              </button>
              
              {profile?.family_id && (
                <button
                  onClick={() => setActiveTab('family')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'family'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4" />
                    <span>Movimientos Familiares</span>
                  </div>
                </button>
              )}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="mt-8">
          {activeTab === 'personal' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Transactions List */}
              <div className="lg:col-span-2">
                {/* Filtros y controles */}
                {transactions.length > 0 && (
                  <div className="mb-6 space-y-4">
                    {/* Filtro por mes */}
                    <MonthFilter
                      transactions={transactions}
                      selectedMonth={selectedMonth}
                      onMonthChange={setSelectedMonth}
                    />
                    
                    {/* Selector de vista */}
                    <div className="bg-white rounded-lg shadow p-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900">
                          Vista de transacciones
                        </h3>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setViewMode('grouped')}
                            className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm ${
                              viewMode === 'grouped'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <Calendar className="h-4 w-4" />
                            <span>Por mes</span>
                          </button>
                          
                          <button
                            onClick={() => setViewMode('list')}
                            className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm ${
                              viewMode === 'list'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <List className="h-4 w-4" />
                            <span>Lista</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Lista de transacciones */}
                {filteredTransactions.length === 0 ? (
                  <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-12 text-center">
                      <PieChart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">
                        {selectedMonth 
                          ? 'No tienes movimientos en este mes'
                          : 'No tienes movimientos aún. ¡Añade tu primer ingreso o gasto!'
                        }
                      </p>
                      {!selectedMonth && (
                        <button
                          onClick={() => setShowAddTransactionModal(true)}
                          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Añadir Movimiento
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {viewMode === 'grouped' ? (
                      <TransactionsByMonth
                        transactions={filteredTransactions}
                        onDelete={handleDeleteTransaction}
                        showUserInfo={false}
                      />
                    ) : (
                      <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                          <h3 className="text-lg font-medium text-gray-900">
                            Movimientos Recientes
                          </h3>
                        </div>
                        
                        <div className="divide-y divide-gray-200">
                          {filteredTransactions.map((transaction) => (
                            <TransactionCard
                              key={transaction.id}
                              transaction={transaction}
                              onDelete={handleDeleteTransaction}
                              showUserInfo={false}
                              showMonth={true}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Family Section */}
              <div className="lg:col-span-1">
                <FamilySection 
                  profile={profile}
                  onFamilyUpdate={refreshProfile}
                />
              </div>
            </div>
          )}

          {activeTab === 'family' && profile?.family_id && (
            <div className="space-y-6">
              {/* Filtros para transacciones familiares */}
              {familyTransactions.length > 0 && (
                <div className="space-y-4">
                  <MonthFilter
                    transactions={familyTransactions}
                    selectedMonth={selectedMonth}
                    onMonthChange={setSelectedMonth}
                  />
                  
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900">
                        Vista de transacciones familiares
                      </h3>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setViewMode('grouped')}
                          className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm ${
                            viewMode === 'grouped'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <Calendar className="h-4 w-4" />
                          <span>Por mes</span>
                        </button>
                        
                        <button
                          onClick={() => setViewMode('list')}
                          className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm ${
                            viewMode === 'list'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <List className="h-4 w-4" />
                          <span>Lista</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Transacciones familiares */}
              {filteredTransactions.length === 0 ? (
                <div className="bg-white rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                      Movimientos de la Familia
                    </h3>
                    <p className="text-sm text-gray-600">
                      Movimientos de todos los miembros de {profile.families?.name}
                    </p>
                  </div>
                  
                  <div className="px-6 py-12 text-center">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">
                      {selectedMonth 
                        ? 'No hay movimientos familiares en este mes'
                        : 'No hay movimientos familiares aún.'
                      }
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {viewMode === 'grouped' ? (
                    <TransactionsByMonth
                      transactions={filteredTransactions}
                      onDelete={null} // No se pueden eliminar transacciones de otros
                      showUserInfo={true}
                    />
                  ) : (
                    <div className="bg-white rounded-lg shadow">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">
                          Movimientos de la Familia
                        </h3>
                        <p className="text-sm text-gray-600">
                          Movimientos de todos los miembros de {profile.families?.name}
                        </p>
                      </div>
                      
                      <div className="divide-y divide-gray-200">
                        {filteredTransactions.map((transaction) => (
                          <TransactionCard
                            key={transaction.id}
                            transaction={transaction}
                            onDelete={null} // No se pueden eliminar transacciones de otros
                            showUserInfo={true}
                            showMonth={true}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modales */}
      {showInitialMoneyModal && (
        <InitialMoneyModal
          currentAmount={profile?.initial_money || 0}
          onSave={handleUpdateInitialMoney}
          onClose={() => setShowInitialMoneyModal(false)}
        />
      )}

      {showAddTransactionModal && (
        <AddTransactionModal
          categories={categories}
          onSave={handleAddTransaction}
          onClose={() => setShowAddTransactionModal(false)}
        />
      )}
    </div>
  )
}

export default Dashboard